import React, { useState, useMemo, useCallback, useEffect } from "react";
import { FaSort, FaSearch, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { useAuth } from "../../hooks/useAuth";
import { useUnits } from "../../hooks/useUnits";
import { useIsMobile } from "../../hooks/useIsMobile";
import Modal from "../../components/forms/Modal";
import UnitForm from "../../components/forms/UnitForm";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import ErrorMessage from "../../components/common/ErrorMessage";
import "./Units.css";

const Units = () => {
  const { user, isAdmin } = useAuth();
  const {
    units,
    loading,
    error,
    addUnit,
    updateUnit,
    deleteUnit,
    getParentUnitName,
  } = useUnits();

  const isMobile = useIsMobile();

  // Permissions
  const canManageUnits = isAdmin;
  const canViewUnits = isAdmin || user?.permissions === "parentsUser";

  // State
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [editingUnit, setEditingUnit] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [operationLoading, setOperationLoading] = useState(false);
  const [operationError, setOperationError] = useState("");
  const [sortField, setSortField] = useState("level");
  const [sortDirection, setSortDirection] = useState("asc");

  // Debounced search to reduce API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Memoized children lookup for performance
  const childrenMap = useMemo(() => {
    const map = new Map();
    units.forEach((unit) => {
      if (unit.parentUnit) {
        if (!map.has(unit.parentUnit)) {
          map.set(unit.parentUnit, []);
        }
        map.get(unit.parentUnit).push(unit);
      }
    });
    return map;
  }, [units]);

  // Optimized filtered and sorted units
  const filteredUnits = useMemo(() => {
    let filtered = units;

    // Apply search filter
    if (debouncedSearchTerm.trim()) {
      const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
      filtered = units.filter(
        (unit) =>
          unit.unitName?.toLowerCase().includes(lowerSearchTerm) ||
          getParentUnitName(unit.parentUnit)
            ?.toLowerCase()
            .includes(lowerSearchTerm) ||
          unit.level?.toString().includes(lowerSearchTerm) ||
          unit.status?.toLowerCase().includes(lowerSearchTerm)
      );
    }

    // Apply sorting
    return filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "parentUnit") {
        aValue = getParentUnitName(a.parentUnit);
        bValue = getParentUnitName(b.parentUnit);
      }

      // Handle null/undefined values
      aValue = aValue ?? "";
      bValue = bValue ?? "";

      // Convert to strings for comparison
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      const result = aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
      return sortDirection === "asc" ? result : -result;
    });
  }, [units, debouncedSearchTerm, getParentUnitName, sortField, sortDirection]);

  // Event handlers
  const handleEdit = useCallback(
    (unit) => {
      const children = childrenMap.get(unit.unitId);
      if (children?.length) {
        const childrenNames = children
          .map((child) => child.unitName)
          .join(", ");
        alert(
          `🚫 לא ניתן לערוך יחידה שיש לה יחידות בנות\n\nיחידות בנות: ${childrenNames}\n\nיש למחוק תחילה את כל היחידות הבנות לפני עריכת היחידה האב`
        );
        return;
      }
      setEditingUnit(unit);
    },
    [childrenMap]
  );

  const handleAddUnit = useCallback(
    async (formData) => {
      setOperationLoading(true);
      setOperationError("");
      const result = await addUnit(formData);
      if (result.success) {
        setShowAddModal(false);
      } else {
        setOperationError(result.error);
      }
      setOperationLoading(false);
    },
    [addUnit]
  );

  const handleUpdateUnit = useCallback(
    async (formData) => {
      setOperationLoading(true);
      setOperationError("");
      const result = await updateUnit(editingUnit.unitId, formData);
      if (result.success) {
        setEditingUnit(null);
      } else {
        setOperationError(result.error);
      }
      setOperationLoading(false);
    },
    [updateUnit, editingUnit]
  );

  const handleDeleteUnit = useCallback(
    async (unitId) => {
      const children = childrenMap.get(unitId);
      if (children?.length) {
        const childrenNames = children
          .map((child) => child.unitName)
          .join(", ");
        alert(
          `🚫 לא ניתן למחוק יחידה שיש לה יחידות בנות\n\nיחידות בנות: ${childrenNames}\n\nיש למחוק תחילה את כל היחידות הבנות לפני מחיקת היחידה האב`
        );
        return;
      }

      if (
        window.confirm(
          "האם אתה בטוח שברצונך למחוק יחידה זו?\n\nפעולה זו לא ניתנת לביטול!"
        )
      ) {
        setOperationLoading(true);
        setOperationError("");
        const result = await deleteUnit(unitId);
        if (!result.success) {
          setOperationError(result.error);
        }
        setOperationLoading(false);
      }
    },
    [deleteUnit, childrenMap]
  );

  const handleCloseModal = useCallback(() => {
    setEditingUnit(null);
    setShowAddModal(false);
    setOperationError("");
  }, []);

  const handleSort = useCallback(
    (field) => {
      setSortField(field);
      setSortDirection((prev) =>
        sortField === field && prev === "asc" ? "desc" : "asc"
      );
    },
    [sortField]
  );

  // Early returns for better performance
  if (!canViewUnits) {
    return (
      <div className="unit-management-container">
        <div className="access-denied">
          <h2>🚫 אין לך הרשאה לגשת לעמוד זה</h2>
          <p>רק מנהלים ומשתמשי מחלקת קשר יכולים לצפות ביחידות</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="unit-management-container">
        <LoadingSpinner message="טוען יחידות..." />
      </div>
    );
  }

  const sortOptions = [
    { value: "level", label: "רמה" },
    { value: "unitName", label: "שם היחידה" },
    { value: "parentUnit", label: "יחידה אב" },
    { value: "status", label: "סטטוס" },
  ];

  const renderMobileCards = () => {
    if (filteredUnits.length === 0) {
      return (
        <div className="units-empty-state">
          {searchTerm
            ? "לא נמצאו יחידות התואמות לחיפוש"
            : "אין יחידות במערכת"}
        </div>
      );
    }

    return filteredUnits.map((unit) => {
      const hasChildren = childrenMap.has(unit.unitId);
      return (
        <div key={unit.unitId} className="unit-card">
          <div className="unit-card-header">
            <span className="unit-card-name">{unit.unitName || "לא זמין"}</span>
            <span
              className={`status-badge ${
                unit.status === "active" ? "status-active" : "status-inactive"
              }`}
            >
              {unit.status === "active" ? "פעיל" : "לא פעיל"}
            </span>
          </div>
          <div className="unit-card-body">
            <div className="unit-card-row">
              <span className="unit-card-label">יחידה אב:</span>
              <span className="unit-card-value">
                {getParentUnitName(unit.parentUnit)}
              </span>
            </div>
            <div className="unit-card-row">
              <span className="unit-card-label">רמה:</span>
              <span className="unit-card-value">{unit.level ?? "לא זמין"}</span>
            </div>
          </div>
          {canManageUnits && !hasChildren && (
            <div className="unit-card-actions">
              <button
                className="edit-btn"
                onClick={() => handleEdit(unit)}
                disabled={operationLoading}
              >
                <FaEdit /> ערוך
              </button>
              <button
                className="delete-btn"
                onClick={() => handleDeleteUnit(unit.unitId)}
                disabled={operationLoading}
              >
                <FaTrash /> מחק
              </button>
            </div>
          )}
          {!canManageUnits && (
            <div className="unit-card-actions">
              <span className="view-only">צפייה בלבד</span>
            </div>
          )}
        </div>
      );
    });
  };

  return (
    <div id="units-page-scope" className="unit-management-container">
      {error && <ErrorMessage message={error} />}
      {operationError && (
        <ErrorMessage
          message={operationError}
          onDismiss={() => setOperationError("")}
        />
      )}

      <div className="unit-management-header">
        <h1>ניהול יחידות</h1>
        {canManageUnits && (
          <button
            className="add-unit-btn"
            onClick={() => setShowAddModal(true)}
            disabled={operationLoading}
          >
            <FaPlus />
            הוסף יחידה
          </button>
        )}
      </div>

      <div className="search-container">
        <div className="search-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="חפש בכל השדות..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
      </div>

      {isMobile && (
        <div className="mobile-sort-container">
          <label htmlFor="mobile-sort">מיון:</label>
          <select
            id="mobile-sort"
            value={sortField}
            onChange={(e) => handleSort(e.target.value)}
            className="mobile-sort-select"
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} {sortField === opt.value ? (sortDirection === "asc" ? "↑" : "↓") : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      {isMobile ? (
        <div className="units-cards-container">{renderMobileCards()}</div>
      ) : (
        <div className="units-table-container">
          <table className="units-table">
            <thead>
              <tr>
                <th
                  onClick={() => handleSort("unitName")}
                  style={{ cursor: "pointer" }}
                >
                  <FaSort /> שם היחידה
                  {sortField === "unitName" && (
                    <span style={{ marginRight: "5px" }}>
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("parentUnit")}
                  style={{ cursor: "pointer" }}
                >
                  <FaSort /> יחידה אב
                  {sortField === "parentUnit" && (
                    <span style={{ marginRight: "5px" }}>
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("level")}
                  style={{ cursor: "pointer" }}
                >
                  <FaSort /> רמה
                  {sortField === "level" && (
                    <span style={{ marginRight: "5px" }}>
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th
                  onClick={() => handleSort("status")}
                  style={{ cursor: "pointer" }}
                >
                  <FaSort /> סטטוס
                  {sortField === "status" && (
                    <span style={{ marginRight: "5px" }}>
                      {sortDirection === "asc" ? "↑" : "↓"}
                    </span>
                  )}
                </th>
                <th>פעולות</th>
              </tr>
            </thead>
            <tbody>
              {filteredUnits.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    style={{ textAlign: "center", padding: "20px" }}
                  >
                    {searchTerm
                      ? "לא נמצאו יחידות התואמות לחיפוש"
                      : "אין יחידות במערכת"}
                  </td>
                </tr>
              ) : (
                filteredUnits.map((unit) => {
                  const hasChildren = childrenMap.has(unit.unitId);
                  return (
                    <tr key={unit.unitId}>
                      <td>{unit.unitName || "לא זמין"}</td>
                      <td>{getParentUnitName(unit.parentUnit)}</td>
                      <td>{unit.level ?? "לא זמין"}</td>
                      <td>
                        <span
                          className={`status-badge ${
                            unit.status === "active"
                              ? "status-active"
                              : "status-inactive"
                          }`}
                        >
                          {unit.status === "active" ? "פעיל" : "לא פעיל"}
                        </span>
                      </td>
                      <td className="actions">
                        {canManageUnits ? (
                          <>
                            {!hasChildren && (
                              <button
                                className="edit-btn"
                                onClick={() => handleEdit(unit)}
                                title="ערוך"
                                disabled={operationLoading}
                              >
                                <FaEdit />
                              </button>
                            )}
                            {!hasChildren && (
                              <button
                                className="delete-btn"
                                onClick={() => handleDeleteUnit(unit.unitId)}
                                title="מחק"
                                disabled={operationLoading}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="view-only">צפייה בלבד</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {canManageUnits && (
        <>
          <Modal
            isOpen={!!editingUnit}
            onClose={handleCloseModal}
            title="עריכת יחידה"
          >
            <UnitForm
              initialValues={{
                unitName: editingUnit?.unitName || "",
                parentUnit: editingUnit?.parentUnit || "",
                level: editingUnit?.level || 0,
                status: editingUnit?.status || "active",
              }}
              onSubmit={handleUpdateUnit}
              onCancel={handleCloseModal}
              submitText="שמור שינויים"
              isEditing={true}
              existingUnits={units}
            />
          </Modal>

          <Modal
            isOpen={showAddModal}
            onClose={handleCloseModal}
            title="הוספת יחידה חדשה"
          >
            <UnitForm
              initialValues={{
                unitName: "",
                parentUnit: "",
                level: 0,
                status: "active",
              }}
              onSubmit={handleAddUnit}
              onCancel={handleCloseModal}
              submitText="הוסף יחידה"
              isEditing={false}
              existingUnits={units}
            />
          </Modal>
        </>
      )}
    </div>
  );
};

export default Units;
